import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Input, Label, Modal, Select } from '@iwoca/orion';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { usePostIntroducer } from 'lendingApi/lendingApiHooks';
import { FormProvider, useForm } from 'react-hook-form';
import { showSuccessToast } from '../../UserManagementToast';
import { UserPermissionsFields } from '../UserPermissionsFields/UserPermissionsFields';
import {
  addUserSchema,
  TAddUserFormValues,
  TITLE_OPTIONS,
} from './AddNewUserModal.helpers';
import styles from './AddNewUserModal.module.css';

type TProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AddNewUserModal = ({ isOpen, onClose }: TProps) => {
  const { introducerCompany } = useIntroducerData();
  const {
    postIntroducer,
    isPending,
    isError,
    reset: resetMutation,
  } = usePostIntroducer();

  const methods = useForm<TAddUserFormValues>({
    defaultValues: {
      title: TITLE_OPTIONS[0],
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      permissions: introducerCompany?.default_permissions,
    },
    resolver: yupResolver(addUserSchema),
  });

  const {
    register,
    formState: { errors },
  } = methods;

  // Reseed a clean form each time the modal opens, once defaults have loaded.
  useEffect(() => {
    if (!isOpen) return;
    methods.reset({
      title: TITLE_OPTIONS[0],
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      permissions: introducerCompany?.default_permissions,
    });
  }, [isOpen, introducerCompany, methods]);

  const onSubmit = (values: TAddUserFormValues) => {
    if (!introducerCompany) return;
    resetMutation();

    postIntroducer(
      {
        body: {
          data: {
            ...values,
            introducer_company_id: introducerCompany.id,
          },
        },
      },
      {
        onSuccess: () => {
          showSuccessToast('User added successfully');
          onClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add new user" size="large">
      <FormProvider {...methods}>
        <form
          className={styles.form}
          onSubmit={methods.handleSubmit(onSubmit)}
          // prevents native validation messages
          noValidate
        >
          <div className={styles.nameGrid}>
            <div className={styles.field}>
              <Label htmlFor="add-user-title">Title</Label>
              <Select
                {...register('title')}
                id="add-user-title"
                error={errors.title?.message}
              >
                {TITLE_OPTIONS.map((title) => (
                  <option key={title} value={title}>
                    {title}
                  </option>
                ))}
              </Select>
            </div>
            <div className={styles.field}>
              <Label htmlFor="add-user-first-name">First name</Label>
              <Input
                {...register('first_name')}
                id="add-user-first-name"
                error={errors.first_name?.message}
              />
            </div>
            <div className={styles.field}>
              <Label htmlFor="add-user-last-name">Last name</Label>
              <Input
                {...register('last_name')}
                id="add-user-last-name"
                error={errors.last_name?.message}
              />
            </div>
          </div>
          <div className={styles.field}>
            <Label htmlFor="add-user-email">Email</Label>
            <Input
              {...register('email')}
              id="add-user-email"
              type="email"
              error={errors.email?.message}
            />
          </div>
          <div className={styles.field}>
            <Label htmlFor="add-user-phone">Phone</Label>
            <Input
              {...register('phone_number')}
              id="add-user-phone"
              type="tel"
              error={errors.phone_number?.message}
            />
          </div>
          <UserPermissionsFields />
          {isError && (
            <p className={styles.error}>Couldn’t add user. Please try again.</p>
          )}
          <div className={styles.footer}>
            <Button
              variant="tertiary"
              type="button"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={isPending}>
              Add user
            </Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};
